/**
 * Backup Storage Assignment API client (Spec 201 — US3)
 *
 * Bulk assignment (per-entity results), default-assignment rule, SP grant + role.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import apiClient from '../services/api';

// 'workspace' (Spec 205) is a backup target for trigger + schedule + listing, but NOT for storage
// assignment (its destination is the governing plan's storage). The Backups list shows workspace
// rows read-only for storage; "Back up now" + reschedule are supported.
export type BackupEntityType = 'lakehouse' | 'warehouse' | 'workspace';

export interface AssignResult {
  entityType: BackupEntityType;
  entityId: string;
  action: 'assign' | 'override' | 'skip-existing';
  reason: string;
  status: 'succeeded' | 'skipped' | 'failed';
  error?: string;
}

export interface GrantResult {
  entityType: BackupEntityType;
  entityId: string;
  workspaceId: string | null;
  role: 'Contributor' | 'Member' | 'Admin';
  status: 'granted' | 'requested' | 'failed';
  detail?: string;
}

export interface AssignableEntity {
  entityType: BackupEntityType;
  entityId: string;
  name: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  envHint: 'prod' | 'dev' | 'uat' | 'test' | null;
  assigned: boolean;
  storageAccountId: string | null;
  storageAccountName: string | null;
  /** Optional human description — rendered on a SECOND LINE beneath the account name. */
  storageAccountDisplayName?: string | null;
  // Spec 214 T074: the governing plan resolved in the SAME backend query - kills the
  // Management-tab per-plan detail fan-out (one plan per entity via UX_bpt_entity).
  planId: string | null;
  planName: string | null;
  planStatus: string | null;
}

export interface StorageAccountOption {
  id: string;
  /** The Azure account name — THE identity. Never a display name (owner rule, 2026-08-10). */
  name: string;
  /** Optional human description, rendered BESIDE the identity via storageAccountLabel(). */
  displayName?: string | null;
  subscription: string | null;
  region: string | null;
  usedGb: number | null;
  allocations: number;
  envHint: 'prod' | 'dev' | 'uat' | 'test' | null;
}

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup';

/** Per-assigned-entity backup config (destination + governing plan's cadence + the EDITABLE per-backup
 *  start time + last/next). startHour/startMinute come from the per-entity schedule row the executor runs. */
export interface BackupConfig {
  entityType: BackupEntityType;
  entityId: string;
  name: string | null;
  workspaceName: string | null;
  envHint: 'prod' | 'dev' | 'uat' | 'test' | null;
  destination: string | null;
  planId: string | null;
  planName: string | null;
  scheduleName: string | null;
  cadence: string | null;
  retentionName: string | null;
  retentionPolicyId: string | null;
  retentionAssignedAtSource: boolean;
  lastBackupAt: string | null;
  nextRunAt: string | null;
  hasSchedule: boolean;
  startHour: number | null;
  startMinute: number | null;
  scheduleHours: number[];
  protectionTier: string | null;
}

export const assignmentApi = {
  assignableEntities: async (): Promise<AssignableEntity[]> =>
    (await apiClient.get<Wrapped<AssignableEntity[]>>(`${BASE}/assignable-entities`)).data,

  backupConfigs: async (): Promise<BackupConfig[]> =>
    (await apiClient.get<Wrapped<BackupConfig[]>>(`${BASE}/backup-configs`)).data,

  /** Materialize per-entity schedule rows from active plans (create-missing; idempotent). */
  ensureSchedules: async (): Promise<{ created: number }> =>
    (await apiClient.post<Wrapped<{ created: number }>>(`${BASE}/schedules/ensure`, {})).data,

  /** Shift a backup's START TIME (HH:MM) to react to the heatmap. */
  setStartTime: async (
    entityType: BackupEntityType,
    entityId: string,
    hour: number,
    minute: number
  ): Promise<{ updated: number }> =>
    (await apiClient.put<Wrapped<{ updated: number }>>(`${BASE}/schedules/start-time`, { entityType, entityId, hour, minute })).data,

  storageAccounts: async (): Promise<StorageAccountOption[]> =>
    (await apiClient.get<Wrapped<StorageAccountOption[]>>(`${BASE}/storage-accounts`)).data,

  bulkAssign: async (
    entityRefs: Array<{ entityType: BackupEntityType; entityId: string }>,
    storageAccountId: string,
    override: boolean
  ): Promise<AssignResult[]> =>
    (await apiClient.post<Wrapped<AssignResult[]>>(`${BASE}/storage-assignments/bulk`, { entityRefs, storageAccountId, override })).data,

  // (getDefaultRule/setDefaultRule/roleFor/storageBinding removed — Spec 214 T058: dead
  // client methods with zero UI consumers since the Spec-201 cutover.)

  grant: async (entityType: BackupEntityType, entityId: string, workspaceId?: string): Promise<GrantResult> =>
    (await apiClient.post<Wrapped<GrantResult>>(`${BASE}/grant/${entityType}/${entityId}`, { workspaceId })).data,

  triggerBackup: async (
    entityType: BackupEntityType,
    entityId: string,
    backupType: 'full' | 'incremental' = 'full'
  ): Promise<{ launched: boolean; executionName?: string; reason?: string }> => {
    const r = await apiClient.post<Wrapped<{ launched: boolean; executionName?: string; reason?: string }>>(
      `${BASE}/trigger/${entityType}/${entityId}`,
      { backupType }
    );
    return r.data;
  },

  // ── Spec 208 — transparent storage switching, bulk reassignment + RPO tier, change history ──

  /** Bulk-reassign the destination storage account (US2). Non-destructive to existing points. */
  reassignStorage: async (req: ReassignStorageRequest): Promise<BulkOpResponse> =>
    (await apiClient.post<Wrapped<BulkOpResponse>>(`${BASE}/storage/reassign`, req)).data,

  /** Bulk-set the source RPO/protection tier (US3). */
  assignRpoTier: async (req: AssignRpoTierRequest): Promise<BulkOpResponse> =>
    (await apiClient.post<Wrapped<BulkOpResponse>>(`${BASE}/rpo-tier/assign`, req)).data,

  /** The admin command to make a destination backup-capable (Spec 208 — app can't self-grant). */
  storagePrepCommand: async (storageAccountId: string): Promise<StoragePrepCommand> =>
    (await apiClient.get<Wrapped<StoragePrepCommand>>(`${BASE}/storage/${storageAccountId}/prep-command`)).data,

  /** Storage/RPO change history for compliance (US4). */
  changes: async (params?: { entityType?: BackupEntityType; entityId?: string; changeType?: BackupChangeType; from?: string; to?: string }): Promise<BackupChange[]> => {
    const q = new URLSearchParams();
    if (params?.entityType) q.set('entityType', params.entityType);
    if (params?.entityId) q.set('entityId', params.entityId);
    if (params?.changeType) q.set('changeType', params.changeType);
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const qs = q.toString();
    const r = await apiClient.get<Wrapped<{ changes: BackupChange[] }>>(`${BASE}/changes${qs ? `?${qs}` : ''}`);
    return r.data.changes;
  },
};

// ── Spec 208 types ──────────────────────────────────────────────────────────
export type StorageBindingState = 'recorded' | 'fallback' | 'unverified';
export type BackupChangeType = 'storage' | 'rpo_tier';

export interface BulkSelection {
  ids?: Array<{ entityType: BackupEntityType; entityId: string }>;
  filter?: { workspaceId?: string; capacityId?: string; clientId?: string; entityType?: BackupEntityType };
}

export interface BulkItemResult {
  entityType: BackupEntityType;
  entityId: string;
  status: 'succeeded' | 'skipped' | 'failed';
  reason?: string;
  oldAccount?: string | null;
  newAccount?: string | null;
  grant?: 'granted' | 'skipped' | 'failed';
  oldTier?: string | null;
  newTier?: string | null;
  scheduleReconfigured?: boolean;
  retentionReconfigured?: boolean;
}

export interface BulkOpResponse {
  batchId: string;
  summary: { total: number; succeeded: number; skipped: number; failed: number };
  results: BulkItemResult[];
}

export interface ReassignStorageRequest {
  targetStorageAccountId: string;
  selection: BulkSelection;
  reason?: string;
  dryRun?: boolean;
}

export interface AssignRpoTierRequest {
  rpoTier: string;
  selection: BulkSelection;
  reason?: string;
  dryRun?: boolean;
}

export interface StoragePrepCommand {
  accountName: string;
  env: string;
  armResourceId: string | null;
  spAppId: string;
  fullBlueprintCommand: string;
  minimalRbacCommand: string | null;
  cloudShellUrl: string;
  likelyProvisioned: boolean;
  loginHint: string;
}

export interface EntityBinding {
  entityType: BackupEntityType;
  entityId: string;
  currentPrimaryAccount: string | null;
  recoveryPoints: Array<{ runId: string; createdAt: string | null; storageAccount: string | null; bindingState: StorageBindingState }>;
}

export interface BackupChange {
  id: string;
  batchId: string;
  changeType: BackupChangeType;
  entityType: BackupEntityType;
  entityId: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string | null;
  changedBy: string | null;
  changedByName?: string | null;
  changedAt: string;
}
